import {
  DeleteOutlined,
  DownSquareOutlined,
  PlusOutlined,
  UpCircleOutlined,
  UpSquareOutlined,
} from "@ant-design/icons";
import { IDeliverableFormat } from "@shared/lib/types";
import { Button } from "@shared/ui/Button";
import {
  Card,
  Divider,
  Flex,
  Form,
  FormListFieldData,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tag,
} from "antd";
import { useEffect, useState } from "react";
import useUpdateDeliverableFormat from "../lib/hooks/useUpdateDeliverableFormat";
import { ToastService } from "@shared/lib/services";
import useUpdateDeliverableSubtype from "../lib/hooks/useUpdateDeliverableSubtype";

const { TextArea } = Input;

export default function FormatSubTypeCard({
  deliverableId,
  subField,
  format,
  isEdit,
  removeSub,
}: {
  deliverableId: string;
  subField: FormListFieldData;
  format: IDeliverableFormat;
  isEdit: boolean;
  removeSub: (index: number) => void;
}) {
  const form = Form.useFormInstance();
  const [newSubtypeValue, setNewSubtypeValue] = useState<string>("");
  const [availableValues, setAvailableValues] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const {
    updateDeliverableFormat,
    isUpdateDeliverableFormatLoading,
    isUpdateDeliverableFormatSuccess,
    updateDeliverableFormatError,
    updateDeliverableFormatVariables,
  } = useUpdateDeliverableFormat();

  const {
    updateDeliverableSubtype,
    isUpdateDeliverableSubtypeLoading,
    isUpdateDeliverableSubtypeSuccess,
    updateDeliverableSubtypeError,
    updateDeliverableSubTypeVariables,
  } = useUpdateDeliverableSubtype();

  useEffect(() => {
    if (updateDeliverableFormatError) {
      ToastService.error(updateDeliverableFormatError?.message);
    } else if (isUpdateDeliverableFormatSuccess) {
      ToastService.success("Deliverable format updated successfully.");
    }
  }, [isUpdateDeliverableFormatSuccess, updateDeliverableFormatError]);

  useEffect(() => {
    if (updateDeliverableSubtypeError) {
      ToastService.error(updateDeliverableSubtypeError?.message);
    } else if (isUpdateDeliverableSubtypeSuccess) {
      ToastService.success("Deliverable subtype updated successfully.");
    }
  }, [isUpdateDeliverableSubtypeSuccess, updateDeliverableSubtypeError]);

  const addNewValues = (name: string, subTypeName: number) => {
    if (!name.trim() || availableValues.includes(name)) {
      return;
    }

    const updatedValues = [...availableValues, name];
    const formVals =
      form.getFieldValue(["subTypes", subTypeName, "values"]) || [];
    form.setFieldValue(
      ["subTypes", subTypeName, "values"],
      [...formVals, name]
    );
    setAvailableValues(updatedValues);
    setNewSubtypeValue("");
    setDropdownOpen(false);
  };

  const saveSubtype = async (fieldIdx: number) => {
    try {
      await form.validateFields([
        ["subTypes", fieldIdx, "name"],
        ["subTypes", fieldIdx, "values"],
        ["subTypes", fieldIdx, "description"],
      ]);
      updateDeliverableFormat({
        deliverableConfiguratorId: deliverableId,
        deliverableConfiguratorDeliverableId: format?._id || "",
        addSubType: form.getFieldValue(["subTypes", fieldIdx]) || {},
      });
    } catch (err) {
      console.log("Error");
    }
  };

  const updateSubtype = async (fieldIdx: number) => {
    try {
      await form.validateFields([
        ["subTypes", fieldIdx, "name"],
        ["subTypes", fieldIdx, "values"],
        ["subTypes", fieldIdx, "description"],
      ]);
      updateDeliverableSubtype({
        deliverableConfiguratorId: deliverableId,
        deliverableConfiguratorDeliverableId: format?._id || "",
        subTypeId: format?.subTypes[fieldIdx]?._id || "",
        description: form.getFieldValue(["subTypes", fieldIdx])?.description,
        values: form.getFieldValue(["subTypes", fieldIdx])?.values,
      });
    } catch (err) {
      console.log("Error");
    }
  };

  const toggleArchiveSubtype = (fieldIdx: number, archived: boolean) => {
    updateDeliverableSubtype({
      deliverableConfiguratorId: deliverableId,
      deliverableConfiguratorDeliverableId: format?._id || "",
      subTypeId: format?.subTypes[fieldIdx]?._id || "",
      isArchived: archived,
    });
  };

  return (
    <Card
      key={subField.key}
      size="small"
      style={{
        marginBottom: 12,
        width: "100%",
      }}
      type="inner"
      title={`SubType ${subField.name + 1}`}
      extra={
        <Flex justify="center" align="center">
          {!(isEdit && format?.subTypes?.[subField.name]) ? (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeSub(subField.name)}
            />
          ) : (
            <></>
          )}
          {isEdit && format?.subTypes?.[subField.name] ? (
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Archived"
              checked={
                format?.subTypes?.[subField.name]?.isArchived ? false : true
              }
              onChange={(checked) =>
                toggleArchiveSubtype(subField.name, !checked)
              }
              loading={
                isUpdateDeliverableSubtypeLoading &&
                updateDeliverableSubTypeVariables?.isArchived !== undefined
              }
            />
          ) : (
            <></>
          )}
        </Flex>
      }
    >
      <Form.Item
        name={[subField.name, "name"]}
        label="SubType Name"
        rules={[
          {
            required: true,
            message: "Enter subtype name",
          },
        ]}
      >
        <Input
          placeholder="e.g. Static, Video"
          disabled={isEdit && format?.subTypes?.[subField.name] ? true : false}
        />
      </Form.Item>

      <Form.Item
        name={[subField.name, "description"]}
        label="SubType Description"
      >
        <TextArea
          rows={2}
          placeholder="Describe the subtype"
          // disabled={isEdit && format?.subTypes?.[subField.name] ? true : false}
        />
      </Form.Item>
      <Form.Item label="Values" name={[subField.name, "values"]}>
        <Select
          mode="multiple"
          open={dropdownOpen}
          onDropdownVisibleChange={(open) => setDropdownOpen(open)}
          showSearch
          placeholder="Select or create a value"
          optionFilterProp="children"
          allowClear
          // disabled={isEdit && format?.subTypes?.[subField.name] ? true : false}
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: "8px 0" }} />
              <Space
                style={{
                  padding: "0 8px 4px",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <Input
                  placeholder="Enter new value"
                  value={newSubtypeValue}
                  onChange={(e) => setNewSubtypeValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newSubtypeValue) {
                      e.stopPropagation();

                      e.preventDefault();
                      addNewValues(newSubtypeValue, subField?.name);
                    }
                  }}
                />
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  disabled={!newSubtypeValue}
                  onClick={() => addNewValues(newSubtypeValue, subField?.name)}
                >
                  Add Value
                </Button>
              </Space>
            </>
          )}
          filterOption={(input, option) =>
            (option?.children as unknown as string)
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {availableValues.map((role) => (
            <Select.Option key={role} value={role}>
              {role}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {/* {isEdit && !format?.subTypes?.[subField.name] && ( */}
      <Flex justify="flex-end">
        {isEdit && (
          <Button
            type="primary"
            onClick={() => {
              if (format?.subTypes?.[subField.name]) {
                updateSubtype(subField.name);
              } else {
                saveSubtype(subField.name);
              }
            }}
            loading={
              isEdit &&
              updateDeliverableSubTypeVariables?.isArchived === undefined &&
              (isUpdateDeliverableFormatLoading ||
                isUpdateDeliverableSubtypeLoading)
            }
          >
            {format?.subTypes?.[subField.name]
              ? "Edit SubType"
              : "Save SubType"}
          </Button>
        )}
      </Flex>
      {/* )} */}
    </Card>
  );
}
